import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { ParamEditor } from "./AnthropometricEditor";
import AnthropometricService from "./AnthropometricService";
import { PARAMETER_BY_KEY } from "./data";

/**
 * Transcript-facing thin wrapper around the standalone rail's ParamEditor
 * (see AnthropometricEditor.js). Renders one card per captured
 * measurement in the transcript flow: outer chrome + theme + eyebrow,
 * body delegated to ParamEditor.
 *
 * Two seed paths:
 *   1. AI-captured payload carries the value (numericValue OR
 *      valueDisplay). ParamEditor is seeded from the payload; save is
 *      always enabled (`bypassSameAsLatest=true`) so the reviewer's Save
 *      commits the AI-seeded values verbatim.
 *   2. Manual row-click carries only `{parameter}`. Fetch the patient's
 *      current anthropometric doc and seed ParamEditor with the
 *      parameter's persisted Current — matches the standalone rail's
 *      pre-fill so the reviewer starts from the known value, and the
 *      "no changes to save" gate stays on (same as standalone Edit).
 *
 * Standalone rail is unchanged — it continues to embed ParamEditor
 * inside its own accordion card and passes `latestOfThis` from its
 * own fetched doc.
 */

const findParameter = (parameters, key) => {
  for (const p of parameters || []) {
    if (p.parameter === key) return p;
  }
  return null;
};

const AnthropometricForm = ({
  formId,
  patientId,
  initialPayload,
  saving,
  error,
  onSave,
  onReject,
  onClose,
  themeClass,
  isReopen,
}) => {
  const p = initialPayload || {};
  const parameterKey = p.parameter;
  const meta = PARAMETER_BY_KEY[parameterKey];

  // The AI agent emits snake_case (`numeric_value`, `value_display`,
  // `test_date`) via asdict(Suggestion); the frontend body adapter
  // emits camelCase (`numericValue`, `valueDisplay`, `testDate`). Both
  // shapes land in captured.current depending on origin — AI fan-out
  // vs user Save — so read both and let the camelCase form win when
  // present (reviewer edits are always camelCase).
  const payloadNumeric = (() => {
    if (typeof p.numericValue === "number" && !Number.isNaN(p.numericValue)) return p.numericValue;
    if (typeof p.numeric_value === "number" && !Number.isNaN(p.numeric_value)) return p.numeric_value;
    if (typeof p.numeric_value === "string" && p.numeric_value.trim() !== "") {
      const n = Number(p.numeric_value);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  })();
  const payloadValueDisplay = p.valueDisplay || p.value_display || "";
  const payloadUnit = p.unit || "";
  const payloadTestDate = p.testDate || p.test_date || "";

  // "AI-seeded" = the payload carries a real measurement value. When
  // it does, ParamEditor is seeded from the payload and Save is always
  // enabled (the reviewer's commit IS the change).
  const hasAiValue =
    payloadNumeric !== null ||
    (typeof payloadValueDisplay === "string" && payloadValueDisplay.trim() !== "");

  // Manual row-click needs the persisted Current — fetch once on mount.
  // The fetch is skipped for AI-seeded cards (their `latestOfThis` is
  // derived from the payload) and for the "unknown parameter" bail
  // branch below.
  const [fetchedCurrent, setFetchedCurrent] = useState(null);
  const [fetching, setFetching] = useState(!hasAiValue && !!patientId && !!meta);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (hasAiValue || !patientId || !meta) return;
    const svc = new AnthropometricService();
    svc.list(
      patientId,
      (doc) => {
        if (!mounted.current) return;
        const entry = findParameter(doc && doc.parameters, parameterKey);
        setFetchedCurrent(entry ? entry.current : null);
        setFetching(false);
      },
      () => {
        if (!mounted.current) return;
        setFetchedCurrent(null);
        setFetching(false);
      }
    );
  }, [hasAiValue, patientId, meta, parameterKey]);

  if (!meta) {
    return (
      <div className="an-acc">
        <div className={`an-card is-open ${themeClass || ""}`.trim()}>
          <div className="an-card-body">
            <p className="cc-feedback is-err">
              Unknown anthropometric parameter — cannot render editor.
            </p>
            <div className="sec-editor-actions an-editor-actions">
              <button type="button" className="sec-btn sec-btn-ghost" onClick={() => onClose(formId)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Seed source: AI payload wins; else the fetched persisted Current.
  // Missing on both paths → null (blank card). Reads normalised
  // snake_case / camelCase values from above so an AI emission's
  // `numeric_value` isn't lost.
  let latestOfThis = null;
  if (hasAiValue) {
    latestOfThis = {
      numericValue: payloadNumeric,
      unit: payloadUnit,
      valueDisplay: payloadValueDisplay,
      testDate: payloadTestDate
        ? new Date(payloadTestDate).toISOString()
        : new Date().toISOString(),
    };
  } else if (fetchedCurrent) {
    latestOfThis = fetchedCurrent;
  }


  const rootClass = ["an-card", "is-open", themeClass || ""].filter(Boolean).join(" ");
  const displayError = error || "";

  const handleSave = (body) => {
    if (typeof onSave === "function") onSave(body);
  };
  const handleClose = () => onClose(formId);

  return (
    <div className="an-acc">
      <div className={rootClass}>
        {/* ZY-10356 Phase 6 Rule 1 — top-right X close only on re-opens
            (#6/#8). First-opens (#5/#7) leave the footer Reject as the
            sole dismiss affordance. Anthropometric is a fixed-param
            resource so #1-#4 never apply here. Mirrors Addictions / PA /
            CC. */}
        {isReopen ? (
          <button
            type="button"
            className="zy-card-x"
            onClick={handleClose}
            aria-label="Close"
          >
            &times;
          </button>
        ) : null}
        {/* Same shape as the standalone rail's accordion head — kept as
            a button so `.an-acc .an-card-head` styling applies. The
            transcript card is always open, so the click is a no-op
            (the reviewer dismisses via Cancel in the footer). */}
        <button
          type="button"
          className="an-card-head"
          aria-expanded="true"
          onClick={() => {}}
          style={{ cursor: "default" }}
        >
          <span className="an-card-title">{meta.label}</span>
        </button>
        {fetching ? (
          <div className="an-card-body">
            <p className="ait-rail-load">Loading current value…</p>
          </div>
        ) : (
          <ParamEditor
            /* Payload-hashed `key` on the editor itself so a mid-review
               AI re-emit (parent re-renders with new `latestOfThis` but
               React might reuse the ParamEditor instance when the
               wrapper's own key doesn't change) still forces an
               unmount + remount — the useState initializers inside
               ParamEditor re-read `latestOfThis` only on mount. Pairs
               with the parent's `scopedFormId` key: whichever changes
               first wins, so the Weight / Height field always chases
               the latest AI payload. */
            key={`pe::${latestOfThis && latestOfThis.numericValue != null ? latestOfThis.numericValue : ""}::${latestOfThis && latestOfThis.valueDisplay ? latestOfThis.valueDisplay : ""}::${latestOfThis && latestOfThis.unit ? latestOfThis.unit : ""}::${latestOfThis && latestOfThis.testDate ? latestOfThis.testDate : ""}`}
            meta={meta}
            latestOfThis={latestOfThis}
            onSave={handleSave}
            onCancel={typeof onReject === "function" ? onReject : handleClose}
            /* Transcript mode always surfaces "Reject" on the ghost
               button (even when the parent didn't wire an explicit
               onReject — e.g. the manual-click updateCards path for
               cases #5/#7, where the handler falls through to a local
               close). Matches CC/Addictions "Reject" label convention
               so #5-#8 cards look consistent. Standalone (no onSave)
               keeps "Cancel". */
            cancelLabel={typeof onSave === "function" ? "Reject" : "Cancel"}
            submitting={!!saving}
            bypassSameAsLatest={hasAiValue}
            menuClassName="ait-menu"
            /* Anthropometric is a fixed-parameter resource — every card
               is an update of a known slot. Match the Addictions label
               convention (always "Update" in transcript) so the 8-case
               card contract reads consistently. */
            saveLabel="Update"
            savingLabel="Updating…"
            /* ZY-10356 Phase 1 — hands transcript-mode draft key down to
               the shared ParamEditor so its field state survives tab
               switch / minimize. Standalone callers pass no draftKey.
               Scope the draft key to the current payload too so a
               fresh AI re-emit doesn't replay the previous draft. */
            draftKey={`${formId}::${latestOfThis && latestOfThis.numericValue != null ? latestOfThis.numericValue : ""}::${latestOfThis && latestOfThis.valueDisplay ? latestOfThis.valueDisplay : ""}`}
          />
        )}
        {displayError && (
          <div className="cc-feedback is-err an-feedback">{displayError}</div>
        )}
      </div>
    </div>
  );
};

AnthropometricForm.propTypes = {
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  patientId: PropTypes.number,
  initialPayload: PropTypes.object,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onReject: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  themeClass: PropTypes.string,
  /** ZY-10356 Phase 6 — surfaces top-right X-close on reopen cards
   *  (#6/#8). First-opens keep footer Reject only. */
  isReopen: PropTypes.bool,
};

AnthropometricForm.defaultProps = {
  patientId: undefined,
  initialPayload: null,
  saving: false,
  error: "",
  themeClass: "",
  isReopen: false,
};

export default AnthropometricForm;
